import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { harnessService } from '@/services/api/harnessService';
import type { MeasurementDto, SaveMeasurementAnnotationDto } from '@/features/harness/types';
import type { ReferenceLabelValue } from '@/types/referenceLabels';

export interface MeasurementAnnotationPanelProps {
  /** The Batch the Measurement belongs to (needed for the selection-metrics channel-key lookup). */
  batchId: string | null | undefined;
  /** Selected Measurement ids in run order. */
  selectedIds: string[];
  /**
   * The full set of Measurements the caller currently has in hand (Story 2.1, Overall Notes Per
   * Measurement, epics-harness-review-ux-improvements.md) — used ONLY to read the selected
   * Measurement's overall `note` value, since there is no per-Measurement GET endpoint. Channel
   * enumeration stays sourced entirely from `getSelectionMetrics`, never from this prop. Optional,
   * defaulting to `[]`, so existing callers/tests predating this field keep working unmodified.
   */
  measurements?: MeasurementDto[];
  /**
   * Fired after a successful overall-Note PATCH so the caller can write the saved value back into
   * its own data source (the live store / the review query cache). Without this, a later re-seed of
   * the field (on a Measurement re-selection) would revert to the pre-save `note` still carried in
   * the `measurements` prop. Optional — callers that don't wire it simply don't get the write-back.
   */
  onMeasurementNoteSaved?: (measurementId: string, note: string | null) => void;
}

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * The 1-based channel keys present in the single selected Measurement's metrics, ascending —
 * mirrors `CurveParametersView.tsx`'s `unionChannelKeys` numeric sort exactly (AC#1). Channel
 * enumeration is derived solely from `getSelectionMetrics`'s `parametersByChannel` keys, never
 * from `excitationProfileJson` or any other source.
 */
function sortedChannelKeys(parametersByChannel: Record<string, unknown> | undefined): string[] {
  return Object.keys(parametersByChannel ?? {}).sort((a, b) => Number(a) - Number(b));
}

interface AnnotationRowProps {
  channelIndex: number;
  label: ReferenceLabelValue | null | undefined;
  note: string;
  concentrationRaw: string;
  concentrationInvalid: boolean;
  status: RowStatus;
  onLabelChange: (value: ReferenceLabelValue | null) => void;
  onNoteChange: (value: string) => void;
  onNoteBlur: () => void;
  onConcentrationChange: (raw: string) => void;
  onConcentrationBlur: () => void;
}

/**
 * One channel's editable annotation row. Modeled closely on
 * `ReferenceLabelPicker.tsx`'s layout (label caption + toggle group, then concentration + note
 * fields) but not a direct reuse of it — that component is keyed to `channelId`/`pathogenName`
 * and imports production's `@/types/referenceLabels` prop shape/copy. The Harness has no
 * pathogen concept, only positional channel indices, so rows are captioned `CH{channelIndex}`.
 */
function AnnotationRow({
  channelIndex,
  label,
  note,
  concentrationRaw,
  concentrationInvalid,
  status,
  onLabelChange,
  onNoteChange,
  onNoteBlur,
  onConcentrationChange,
  onConcentrationBlur,
}: AnnotationRowProps) {
  return (
    <Box
      data-testid={`annotation-row-${channelIndex}`}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}
    >
      {/* Row 1: channel label + toggle buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ minWidth: 48, flexShrink: 0, fontWeight: 500 }}>
          CH{channelIndex}
        </Typography>
        <ToggleButtonGroup
          value={label ?? null}
          exclusive
          onChange={(_e, val) => onLabelChange(val as ReferenceLabelValue | null)}
          size="small"
          aria-label={`Reference label for CH${channelIndex}`}
        >
          <ToggleButton value="positive" aria-label="Positive">
            Positive
          </ToggleButton>
          <ToggleButton value="negative" aria-label="Negative">
            Negative
          </ToggleButton>
          <ToggleButton value="rejected" aria-label="Rejected">
            Rejected
          </ToggleButton>
        </ToggleButtonGroup>
        {status === 'saving' && <CircularProgress size={14} />}
        {status === 'saved' && (
          <Chip
            label="Saved"
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {status === 'error' && (
          <Typography variant="caption" color="error">
            Save failed
          </Typography>
        )}
      </Box>

      {/* Row 2: concentration input + note input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pl: '56px' }}>
        <TextField
          type="number"
          size="small"
          sx={{ width: 140 }}
          placeholder="cp/mL"
          value={concentrationRaw}
          error={concentrationInvalid}
          helperText={concentrationInvalid ? 'Must be > 0' : undefined}
          onChange={(e) => onConcentrationChange(e.target.value)}
          onBlur={onConcentrationBlur}
          inputProps={{
            min: 0,
            step: 'any',
            'aria-label': `Concentration for CH${channelIndex}`,
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            },
          }}
        />
        <TextField
          size="small"
          sx={{ flex: 1 }}
          placeholder="Note…"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          onBlur={onNoteBlur}
          inputProps={{
            'aria-label': `Note for CH${channelIndex}`,
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            },
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Presentational per-Measurement annotation panel (AC#1-#8): one editable row per channel of the
 * single selected Measurement, backed by the Measurement-Annotation endpoints (Story 2.2). Reads
 * nothing from the store — like `CurveParametersView.tsx`/`ExcitationProfileView.tsx`, it is fed
 * its data via props by both callers (the live `HarnessPage.tsx` and the connection-free
 * `BatchReview.tsx`), with no separate `*View`/`*Panel` split needed for this feature.
 *
 * Shown only when exactly one Measurement is selected (AC#5), mirroring
 * `ExcitationProfileView.tsx`'s established N=1-only precedent — annotation is about one
 * Measurement's channels, never a cross-Measurement comparison.
 */
export function MeasurementAnnotationPanel({
  batchId,
  selectedIds,
  measurements = [],
  onMeasurementNoteSaved,
}: MeasurementAnnotationPanelProps) {
  const measurementId = selectedIds.length === 1 ? selectedIds[0] : null;

  // Latest-value refs read by async callbacks and the seed effect, so neither closes over a stale
  // render: `measurementsRef` lets the seed effect depend on `measurementId` ALONE (a new
  // `measurements` array identity from a live stream must NOT re-seed and wipe in-progress text),
  // and `measurementIdRef` lets an in-flight note save detect that the selection has since moved on.
  const measurementsRef = useRef(measurements);
  measurementsRef.current = measurements;
  const measurementIdRef = useRef(measurementId);
  measurementIdRef.current = measurementId;

  const [channelKeys, setChannelKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [labels, setLabels] = useState<Record<number, ReferenceLabelValue | null>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [concentrationRaw, setConcentrationRaw] = useState<Record<number, string>>({});
  const [concentrationInvalid, setConcentrationInvalid] = useState<Record<number, boolean>>({});
  const [rowStatus, setRowStatus] = useState<Record<number, RowStatus>>({});

  // Measurement-level overall Note (Story 2.1). Read synchronously from the `measurements` prop —
  // no network round-trip, unlike the per-channel state above (there is no per-Measurement GET).
  const [measurementNote, setMeasurementNote] = useState('');
  const [measurementNoteStatus, setMeasurementNoteStatus] = useState<RowStatus>('idle');
  const pendingMeasurementNoteRef = useRef('');

  // Refs mirror `ReferenceLabelsPanel.tsx`'s `pendingLabelsRef`/`pendingNotesRef`/
  // `pendingConcentrationsRef` triad (AC#6) — always read the latest values when building a
  // full-replace save item, never a stale closure over `useState`.
  const pendingLabelsRef = useRef<Record<number, ReferenceLabelValue | null>>({});
  const pendingNotesRef = useRef<Record<number, string>>({});
  const pendingConcentrationsRef = useRef<Record<number, number | undefined>>({});

  useEffect(() => {
    if (!batchId || !measurementId) {
      setChannelKeys([]);
      setLabels({});
      setNotes({});
      setConcentrationRaw({});
      setConcentrationInvalid({});
      setRowStatus({});
      pendingLabelsRef.current = {};
      pendingNotesRef.current = {};
      pendingConcentrationsRef.current = {};
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [metricsResult, annotations] = await Promise.all([
          harnessService.getSelectionMetrics(batchId, [measurementId]),
          harnessService.getMeasurementAnnotations(measurementId),
        ]);
        if (cancelled) return;

        setChannelKeys(sortedChannelKeys(metricsResult[0]?.parametersByChannel));

        // A channel absent from the response starts with empty/undefined values in all three
        // maps (AC#4) — never a synthesized placeholder row.
        const labelMap: Record<number, ReferenceLabelValue | null> = {};
        const noteMap: Record<number, string> = {};
        const concentrationMap: Record<number, string> = {};
        const pendingConcentrationMap: Record<number, number | undefined> = {};
        for (const a of annotations) {
          labelMap[a.channelIndex] = a.referenceLabel;
          noteMap[a.channelIndex] = a.notes ?? '';
          concentrationMap[a.channelIndex] = a.concentration != null ? String(a.concentration) : '';
          pendingConcentrationMap[a.channelIndex] = a.concentration ?? undefined;
        }
        setLabels(labelMap);
        setNotes(noteMap);
        setConcentrationRaw(concentrationMap);
        setConcentrationInvalid({});
        setRowStatus({});
        pendingLabelsRef.current = labelMap;
        pendingNotesRef.current = noteMap;
        pendingConcentrationsRef.current = pendingConcentrationMap;
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load Measurement annotations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [batchId, measurementId]);

  // Seeds the Measurement-level Note whenever the SELECTED Measurement changes — mirrors the
  // reset-on-Measurement-change shape of the effect above, but reads synchronously from the prop
  // instead of fetching (there is no per-Measurement GET, FR7 is PATCH-only). Deps are
  // `measurementId` ALONE, read via `measurementsRef`: on the live HarnessPage the `measurements`
  // array gets a fresh identity every time a Measurement streams in, and depending on it here would
  // re-run this effect and silently wipe a note the user is mid-typing (or one just saved but not
  // yet reflected back into the prop).
  useEffect(() => {
    const current = measurementsRef.current.find((m) => m.id === measurementId)?.note ?? '';
    setMeasurementNote(current);
    setMeasurementNoteStatus('idle');
    pendingMeasurementNoteRef.current = current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementId]);

  const saveChannel = async (channelIndex: number) => {
    if (!measurementId) return;
    // AC#3/#8: a non-positive or NaN Concentration is rejected client-side before ANY request is
    // sent. The pending ref is updated on every keystroke, so an in-progress invalid value can be
    // present even on a save triggered by another field (a Label click or a Note blur) without the
    // Concentration field itself having blurred. Re-run the guard here so no save path can leak a
    // stale ≤0/NaN value to the server.
    const pendingConcentration = pendingConcentrationsRef.current[channelIndex];
    if (
      pendingConcentration !== undefined &&
      (Number.isNaN(pendingConcentration) || pendingConcentration <= 0)
    ) {
      setConcentrationInvalid((prev) => ({ ...prev, [channelIndex]: true }));
      return;
    }
    setRowStatus((prev) => ({ ...prev, [channelIndex]: 'saving' }));
    // AC#6: always send all three current fields for this channel, from the pending refs — never
    // just the one field that changed.
    const item: SaveMeasurementAnnotationDto = {
      channelIndex,
      referenceLabel: pendingLabelsRef.current[channelIndex] ?? null,
      notes: pendingNotesRef.current[channelIndex] ? pendingNotesRef.current[channelIndex] : null,
      concentration: pendingConcentrationsRef.current[channelIndex] ?? null,
    };
    try {
      await harnessService.saveMeasurementAnnotations(measurementId, [item]);
      setRowStatus((prev) => ({ ...prev, [channelIndex]: 'saved' }));
    } catch {
      setRowStatus((prev) => ({ ...prev, [channelIndex]: 'error' }));
    }
  };

  const handleLabelChange = (channelIndex: number, value: ReferenceLabelValue | null) => {
    pendingLabelsRef.current = { ...pendingLabelsRef.current, [channelIndex]: value };
    setLabels((prev) => ({ ...prev, [channelIndex]: value }));
    // Label saves immediately on click — no blur/debounce, matching `ReferenceLabelPicker`'s
    // existing `onChange` firing on click already (AC#3).
    saveChannel(channelIndex);
  };

  const handleNoteChange = (channelIndex: number, value: string) => {
    pendingNotesRef.current = { ...pendingNotesRef.current, [channelIndex]: value };
    setNotes((prev) => ({ ...prev, [channelIndex]: value }));
  };

  const handleNoteBlur = (channelIndex: number) => {
    saveChannel(channelIndex);
  };

  const handleConcentrationChange = (channelIndex: number, raw: string) => {
    setConcentrationRaw((prev) => ({ ...prev, [channelIndex]: raw }));
    // Mirrors `ReferenceLabelPicker.tsx`'s `raw === '' ? undefined : Number(raw)` conversion,
    // updated on every keystroke so any other field's immediate save (e.g. a Label click) always
    // reads the latest in-progress Concentration value.
    const numeric = raw === '' ? undefined : Number(raw);
    pendingConcentrationsRef.current = { ...pendingConcentrationsRef.current, [channelIndex]: numeric };
    if (raw === '' || (numeric !== undefined && !Number.isNaN(numeric) && numeric > 0)) {
      setConcentrationInvalid((prev) => ({ ...prev, [channelIndex]: false }));
    }
  };

  const handleMeasurementNoteChange = (value: string) => {
    pendingMeasurementNoteRef.current = value;
    setMeasurementNote(value);
  };

  const handleMeasurementNoteBlur = async () => {
    // Capture the id this save is FOR at call time. The user can select another Measurement while
    // the PATCH is in flight; without this guard the resolving save would stamp its saved/error
    // status onto whatever Measurement's field is displayed now, and write its note back under the
    // wrong id. Mirrors the `cancelled` guard the metrics-loading effect uses.
    const id = measurementId;
    if (!id) return;
    setMeasurementNoteStatus('saving');
    // Empty text saves as null, mirroring the per-channel Note's
    // `pendingNotesRef.current[channelIndex] ? ... : null` conversion in `saveChannel` above.
    const value = pendingMeasurementNoteRef.current;
    const note = value === '' ? null : value;
    try {
      const updated = await harnessService.updateMeasurementNote(id, { note });
      if (measurementIdRef.current !== id) return;
      setMeasurementNoteStatus('saved');
      // Feed the persisted value back into the caller's data source so a later re-seed of this
      // Measurement's field (on re-selection) shows the saved note, not the stale prop value.
      onMeasurementNoteSaved?.(id, updated.note ?? null);
    } catch {
      if (measurementIdRef.current !== id) return;
      setMeasurementNoteStatus('error');
    }
  };

  const handleConcentrationBlur = (channelIndex: number) => {
    const raw = concentrationRaw[channelIndex] ?? '';
    // AC#3/#8: an empty Concentration input is "cleared" (valid, sends null) — distinct from a
    // non-empty string that parses to a number <= 0 ("invalid", blocks the request).
    if (raw !== '') {
      const numeric = Number(raw);
      if (Number.isNaN(numeric) || numeric <= 0) {
        setConcentrationInvalid((prev) => ({ ...prev, [channelIndex]: true }));
        return;
      }
    }
    setConcentrationInvalid((prev) => ({ ...prev, [channelIndex]: false }));
    saveChannel(channelIndex);
  };

  // Early-return null when not exactly one Measurement is selected (AC#5) — same guard shape as
  // `ExcitationProfileView`. Placed after all hooks so hook order stays stable across renders.
  if (selectedIds.length !== 1 || !batchId) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Measurement-level overall Note (AC#1/#6): renders whenever the N=1 guard above passes,
          independent of channel enumeration/metrics loading/error state — the Note has nothing to
          do with per-channel metrics. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Overall Note
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Overall note for this Measurement…"
            value={measurementNote}
            onChange={(e) => handleMeasurementNoteChange(e.target.value)}
            onBlur={handleMeasurementNoteBlur}
            inputProps={{ 'aria-label': 'Overall Measurement note' }}
          />
          {measurementNoteStatus === 'saving' && <CircularProgress size={14} />}
          {measurementNoteStatus === 'saved' && (
            <Chip
              label="Saved"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {measurementNoteStatus === 'error' && (
            <Typography variant="caption" color="error">
              Save failed
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Measurement Annotations
      </Typography>
      {loading && <CircularProgress size={20} />}
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      {!loading && !error && channelKeys.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No channel data available for this Measurement.
        </Typography>
      )}
      {!loading && !error && channelKeys.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {channelKeys.map((ch) => {
            const channelIndex = Number(ch);
            return (
              <AnnotationRow
                key={ch}
                channelIndex={channelIndex}
                label={labels[channelIndex]}
                note={notes[channelIndex] ?? ''}
                concentrationRaw={concentrationRaw[channelIndex] ?? ''}
                concentrationInvalid={concentrationInvalid[channelIndex] ?? false}
                status={rowStatus[channelIndex] ?? 'idle'}
                onLabelChange={(value) => handleLabelChange(channelIndex, value)}
                onNoteChange={(value) => handleNoteChange(channelIndex, value)}
                onNoteBlur={() => handleNoteBlur(channelIndex)}
                onConcentrationChange={(raw) => handleConcentrationChange(channelIndex, raw)}
                onConcentrationBlur={() => handleConcentrationBlur(channelIndex)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
